import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { required, validationContext } from 'validx'
import { waitForPaint } from '@/features/legends/editor/wait-for-paint'
import type { LegendStatus } from '@/features/legends/legend-status'
import type { LegendStore } from '@/features/legends/LegendStore'
import type { LegendVersion } from '@/features/legends/LegendVersion'
import { formatRelativeTime } from '@/features/legends/relative-time'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { mustHaveMaxLength } from '@/utils/validx-util'
import { ConflictDialogViewModel } from './ConflictDialog/ConflictDialogViewModel'
import { RevertDialogViewModel } from './RevertDialog/RevertDialogViewModel'

/**
 * Options for selecting a version of the legend.
 */
export interface SelectLegendVersionOptions {
  /**
   * Keeps what Discard changes restores.
   */
  keepInitialState?: boolean
}

/**
 * Dependencies of the version manager: the legend store, and what it does
 * with the version being edited.
 */
export interface VersionManagerViewModelOptions {
  legendId: Id
  legendStore: LegendStore
  createAlertRetryWrapper: AlertRetryWrapperFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  getSelectedVersion: () => LegendVersion | null
  /**
   * Whether the legend changed since the editor opened.
   */
  isDirty: () => boolean
  /**
   * Whether the legend is being replaced, like by a revert or an import.
   */
  isBusy: () => boolean
  /**
   * The time, kept current.
   */
  getNow: () => Date
  /**
   * Loads a version into the editor; the latest one when no version is
   * given.
   *
   * @returns The version, or `null` when another selection followed.
   */
  selectLegendVersion: (
    version: number | null,
    options?: SelectLegendVersionOptions,
  ) => Promise<LegendVersion | null>
  /**
   * Saves the legend and checks it.
   *
   * @returns Whether it's ready to publish.
   */
  persistAndCheck: () => Promise<boolean>
  /**
   * @returns Whether it published.
   */
  publish: (versionNotes: string) => Promise<boolean>
  discardLocalChanges: () => Promise<void>
  revertToSelectedVersion: () => Promise<void>
}

const VERSION_NOTES_LENGTH_MESSAGE =
  'Version notes should be between 1 and 500 characters'

/**
 * View model for the legend's versions: picking one, publishing, reverting
 * and conflicts.
 */
export class VersionManagerViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable private accessor _versionNotes = ''

  /**
   * The version picker.
   */
  readonly pickerModal = new ModalViewModel()

  /**
   * The publish panel.
   */
  readonly publishModal = new ModalViewModel()

  readonly conflictVm: ConflictDialogViewModel
  readonly revertVm = new RevertDialogViewModel()
  readonly validation = validationContext(this, {
    versionNotes: [
      required(VERSION_NOTES_LENGTH_MESSAGE),
      mustHaveMaxLength(500, VERSION_NOTES_LENGTH_MESSAGE),
    ],
  })

  /**
   * Loads the legend's versions, latest first.
   */
  readonly versionScroller = new InfiniteScrollProvider<LegendVersion>({
    limit: 10,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._options.createAlertRetryWrapper(
        'Loading versions failed',
        signal,
      )
      return withRetry(() =>
        this._options.legendStore.listLegendVersions(
          this._options.legendId,
          paging,
          signal,
        ),
      )
    },
  })

  constructor(private readonly _options: VersionManagerViewModelOptions) {
    this.conflictVm = new ConflictDialogViewModel(
      () => _options.selectLegendVersion(null),
      _options.createAlertErrorHandler,
    )
  }

  @computed
  get versionNotes() {
    return this._versionNotes
  }

  set versionNotes(value: string) {
    this._versionNotes = value
    this.validation.clearErrors('versionNotes')
  }

  @computed
  get selectedVersion() {
    return this._options.getSelectedVersion()
  }

  /**
   * Whether the selected version is a draft.
   */
  @computed
  get isDraft() {
    return !this.selectedVersion?.published
  }

  @computed
  get isViewingLatestVersion() {
    const selected = this.selectedVersion
    const latest = this.latestVersion
    return !!selected && !!latest && latest.version === selected.version
  }

  /**
   * Whether the selected version is published, and not newer than the
   * published one.
   */
  @computed
  get isViewingPreviousVersion() {
    const selected = this.selectedVersion
    const live = this.latestPublishedVersion
    return !!selected && !!live && live.version >= selected.version
  }

  /**
   * Whether the selected version is the one new jobs use.
   */
  @computed
  get isViewingLiveVersion() {
    return this.latestPublishedVersion === this.selectedVersion
  }

  /**
   * The loaded versions, latest first.
   */
  @computed
  get versions() {
    return this._options.legendStore.versionsFor(this._options.legendId)
  }

  @computed
  get latestVersion(): LegendVersion | null {
    return this.versions.at(0) ?? null
  }

  /**
   * The version new jobs use: the latest version, or the one before a draft.
   */
  @computed
  get latestPublishedVersion(): LegendVersion | null {
    const latest = this.latestVersion
    if (!latest || latest.published) {
      return latest
    }
    return (
      this.versions.find(
        (v) => v.version === latest.version - 1 && v.published,
      ) ?? null
    )
  }

  /**
   * Whether the selected version is the draft and has changes worth
   * discarding.
   */
  @computed
  get canDiscardLocalChanges() {
    return (
      (this._options.isDirty() && this.isViewingLatestVersion) ||
      this.discardLocalChanges.pending
    )
  }

  /**
   * Whether another version can be picked: not while one is loading.
   */
  @computed
  get canSelectVersion() {
    return !this.selectVersion.pending
  }

  /**
   * The version's status among the legend's versions.
   *
   * @param version
   */
  getVersionStatus(version: LegendVersion): LegendStatus {
    if (!version.published) {
      return 'Draft'
    }
    return version === this.latestPublishedVersion ? 'Published' : 'Previous'
  }

  /**
   * When the version was published, or last changed for a draft.
   *
   * @param version
   */
  getVersionDate(version: LegendVersion) {
    return this.getVersionStatus(version) === 'Draft' || !version.datePublished
      ? version.dateModified
      : version.datePublished
  }

  /**
   * How long ago the version was published, or last changed for a draft.
   *
   * @param version
   */
  getVersionDateLabel(version: LegendVersion) {
    return formatRelativeTime(
      this.getVersionDate(version),
      this._options.getNow(),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch().catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the first versions. Two pages, so that short histories reach their
   * end before the picker opens.
   */
  fetch = task(async () => {
    const signal = this._lifecycle.signal
    await this.versionScroller.fetch({ signal })
    void this.versionScroller.more(signal).catch(() => undefined)
    if (this.latestVersion?.version === 1 && !this.versionNotes) {
      this.versionNotes = 'Initial version'
    }
  })

  /**
   * Fetches the versions again, after a save or publish changed them.
   */
  refresh() {
    return this.versionScroller
      .fetch({ signal: this._lifecycle.signal, staleWhileRevalidate: true })
      .catch(() => undefined)
  }

  @action.bound
  togglePicker() {
    if (this.pickerModal.showing) {
      this.pickerModal.close()
    } else {
      this.pickerModal.show()
    }
  }

  /**
   * Opens the publish panel unless the legend is being replaced, or closes it
   * unless it's publishing.
   */
  @action.bound
  togglePublishPanel() {
    if (this.publishModal.showing) {
      if (!this.publish.pending) {
        this.publishModal.close()
      }
    } else if (!this._options.isBusy()) {
      // Saves the legend and checks it while the panel opens.
      this.validation.reset()
      void this._options.persistAndCheck().catch(() => undefined)
      this.publishModal.show()
    }
  }

  /**
   * Opens the version in the editor. A failure is reported by the alert.
   */
  selectVersion = task.resolved(async (version: LegendVersion) => {
    if (this.selectedVersion?.version === version.version) {
      this.pickerModal.close()
      return
    }
    await waitForPaint()
    const selected = await this._options
      .selectLegendVersion(version.version, { keepInitialState: true })
      .catch(
        this._options.createAlertErrorHandler('Loading the version failed'),
      )
      .catch(() => null)
    if (selected) {
      this.pickerModal.close()
    }
  })

  /**
   * Goes back to the latest version.
   */
  showLatestVersion = task.resolved(async () => {
    if (this.latestVersion) {
      await this.selectVersion(this.latestVersion)
    }
  })

  /**
   * Restores the legend as it was when the editor opened. A failure is
   * reported by the alert.
   */
  discardLocalChanges = task.resolved(() =>
    this._options.discardLocalChanges().catch(() => undefined),
  )

  /**
   * Makes the draft a copy of the selected version, once confirmed. A failure
   * is reported by the alert.
   */
  revertToSelectedVersion = task.resolved(async () => {
    if (await this.revertVm.prompt()) {
      await this._revert()
    }
  })

  /**
   * Whether discarding, reverting or publishing is running. Waiting for the
   * revert to be confirmed doesn't count.
   */
  @computed
  get isChangingVersion() {
    return (
      this.discardLocalChanges.pending ||
      this._revert.pending ||
      this.publish.pending
    )
  }

  private readonly _revert = task.resolved(() =>
    this._options.revertToSelectedVersion().catch(() => undefined),
  )

  /**
   * Publishes the draft with the version notes. A failure is reported by the
   * alert.
   */
  publish = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }
    const isPublished = await this._options
      .publish(this.versionNotes)
      .catch(() => false)
    if (isPublished) {
      this.versionNotes = ''
      this.publishModal.close()
    }
  })
}
